import type { Ref } from 'vue';

import type {
  FreightDelivery,
  DeliveryMethodWarehouse,
  FreightDeliveryMethod,
  FreightOrderLine,
  PackagingLine,
  PackagingResponse,
  PackagingOrderLine,
} from '@/data/api/freight/freight.d';
import type { Article } from '@/types/Article';
import type { OrderProductlistRow, ProductlistRow } from '@/types/Productlist';

import _ from 'lodash';
import i18n from '@/languages/i18n';

import { formatWeight, convertUnit, roundUnit } from '@/data/api/products/helpers';
import { getArticleTitle } from '@/helpers';
import { LinePrice, Price, PriceManager, PriceObject, PriceType, Pricing } from './pricemanager';
import { PriceCollection } from '@/products';
import ArticleHelper from './article';
import { LineKey, LineKeyMap } from '@/helpers/linekey';
import { FreightConfiguration } from './freightconfiguration';
import { FreightDeliveryOrderLinePatch } from '@/types/Checkout';
import { getRowRetentionStrategy, RowRetentionStrategy } from './orderprices';

export function splitOrderLinesByFreight(rows: any[], deliveries: FreightDelivery[]): any[][] {
  const list: any[][] = [];
  // Each row needs to be placed in a delivery. Iterate all rows and identify which delivery they belong to.
  for (const row of rows) {
    let deliveryRow: FreightOrderLine | undefined,
      deliveryIndex = -1;
    for (deliveryIndex = 0; deliveryIndex < deliveries.length; deliveryIndex++) {
      deliveryRow = deliveries[deliveryIndex].orderLines.find((orderLine) => orderLine.lineNumber === row.lineNumber);

      if (deliveryRow) break;
    }

    if (!deliveryRow) {
      // TODO: This might be real bad if this can actually happen. Might need to act on it
      console.error("Freight response didn't include order line", row);
      console.error('Skipping order line');
      continue;
    }

    if (!list[deliveryIndex]) list[deliveryIndex] = [];
    list[deliveryIndex].push({
      ...row,
    });
  }

  return list;
}

// Returns a clone of the row if it exists in step1Rows, otherwise undefined
export function matchOrderRow(
  rows: any[],
  orderLine: FreightOrderLine | PackagingOrderLine
): OrderProductlistRow | undefined {
  const lineKey = new LineKey(orderLine);
  let existingRow = _.cloneDeep(
    rows.find((row) => {
      // Underlying rows get their artnr prefixed with SL-
      let artnr = row.itemNumber ?? row.artnr;
      if (artnr?.startsWith('SL-')) {
        artnr = artnr.substring(3);
      }
      row.itemNumber = artnr;
      const rowKey = new LineKey(row);
      return rowKey.isEqual(lineKey);
    })
  );

  // Update itemNumber etc. if we find a match
  if (existingRow) {
    existingRow.itemNumber = orderLine.itemNumber;
    // existingRow.quantity = orderLine.quantity;
    existingRow.unit = existingRow?.unit ?? orderLine.unit; // NOTE: Using the existing unit might break something, but it is needed for 'p2' articles
  }

  return existingRow;
}

export function getPrice(
  row: FreightOrderLine | ProductlistRow,
  type: Exclude<PriceType, PriceType.Line | PriceType.M2>
) {
  let prices = [] as any[];
  const manager = row.price_manager as PriceManager;
  const isProductlistRow = 'needsQuote' in row && 'isBlocked' in row;

  const showM2Price = type == PriceType.Net && (!('unitDetails' in row) || !row.unitDetails);

  // Total line price
  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPriceTotal(type == PriceType.Net ? PriceType.Line : type), // A bit weird, but we actually want the line price here for Net
    },
  });

  // Unit Price (e.g 'alternate' price)
  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPrice(type),
      unit: manager?.getBaseUnit(type),
      parenthesis: true,
    },
  });

  if (showM2Price && manager?.exists(PriceType.M2)) {
    prices.push({
      needsQuote: isProductlistRow ? row.needsQuote : false,
      isBlocked: isProductlistRow ? row.isBlocked : false,
      value: {
        price: type == PriceType.Net ? manager?.getPriceTotal(PriceType.M2) : manager?.getPrice(PriceType.M2),
        // class: ['is-size-8'],
        unit: i18n.global.t('unit.short.m2'),
        parenthesis: true,
      },
    });
  }

  return prices;
}

export function getAddonPrice(
  row: FreightOrderLine | ProductlistRow,
  type: Exclude<PriceType, PriceType.Line | PriceType.M2>,
  showNeedsPrice?: boolean
) {
  const manager = row.price_manager as PriceManager;
  let prices = [] as any[];
  let price = manager?.getPrice(type);
  const isProductlistRow = 'needsQuote' in row && 'isBlocked' in row;
  const needsPrice =
    type === PriceType.Net &&
    !row.price_patch?.conditionalAttributes?.isValid &&
    manager?.getPriceTotal(PriceType.Line).value == 0;

  if (showNeedsPrice && needsPrice) {
    return [
      {
        needsPrice: true,
      },
    ];
  }

  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: manager?.getPriceTotal(type == PriceType.Net ? PriceType.Line : type), // A bit weird, but we actually want the line price here for Net
    },
  });

  prices.push({
    needsQuote: isProductlistRow ? row.needsQuote : false,
    isBlocked: isProductlistRow ? row.isBlocked : false,
    value: {
      price: price,
      unit: manager?.getBaseUnit(type) || row.unit,
      parenthesis: true,
    },
  });

  return prices;
}

export function getDiscounts(row: FreightOrderLine | ProductlistRow) {
  return _.range(1, 7)
    .map((index) => {
      const discount = row.price_manager?.getDiscount(PriceType.Line, index);
      if (!discount) return {};
      return {
        index: index,
        discountFactor: discount.discount,
        discountText: discount.text,
        statusDiscount: discount.status,
      };
    })
    .filter((disc) => parseFloat(disc.discountFactor) > 0)
    .map((disc) => ({
      value: `${i18n.global.t('price.discount')} ${disc.index}: ${disc.discountText}<br>${disc.discountFactor} %`,
      style: 'padding: 0.25rem 0',
    }));
}

export interface RowHandlers {
  cache: Ref<Record<string, Article>>;
  mobile: Ref<boolean>;
  region: Ref<string | undefined>;
  customerNumber: Ref<string | undefined>;
  isM3Order: boolean;
  address?: Ref<string | undefined>;
  warehouse?: Ref<string | undefined>; // Fallback if no warehouse is found on row
  packaging: (row: FreightOrderLine | PackagingLine, art: Article, warehouse: string) => Record<string, any>;
  freight: (row: FreightOrderLine, art: Article, warehouse: string) => Record<string, any>;
  standard: (row: OrderProductlistRow, art: Article, warehouse: string, hidePrices: boolean) => Record<string, any>;
}

export function newFreightRowHandlers({
  cache,
  mobile,
  region,
  customerNumber,
  address = undefined,
  warehouse = undefined,
  isM3Order = false,
  isBendersEmployee = false,
}: {
  cache: Ref<Record<string, Article>>;
  mobile: Ref<boolean>;
  region: Ref<string | undefined>;
  customerNumber: Ref<string | undefined>;
  address?: Ref<string | undefined>;
  warehouse?: Ref<string | undefined>;
  isM3Order: boolean;
  isBendersEmployee: boolean;
}): RowHandlers {
  return {
    cache,
    mobile,
    region,
    customerNumber,
    address,
    warehouse,
    isM3Order,
    packaging: function (row: FreightOrderLine | PackagingLine, art: Article, _warehouse: string) {
      const weight = art.chargeableWeight * Math.ceil(row.quantity);
      const id = row.lineNumber;
      const showNeedsPrice = isBendersEmployee && row.category == 'fee';

      return {
        class: 'dynamic',
        id: id,
        _meta: { addon: true, addon_type: 'pallet', readonly: true },
        line: 'lineNumber' in row ? { value: row.lineNumber } : null,
        name_and_warehouse_status: [
          {
            component: 'name',
            name: 'name',
            label: getArticleTitle(art),
            article: art,
          },
        ],
        quantity_unit_pieces: {
          component: 'string',
          article: null,
          value: `${row.quantity} ${convertUnit(row.unit || 'st', {
            type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
            lowercase: false,
          })}`,
        },
        weight: {
          value: weight ? formatWeight(weight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: getAddonPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: getAddonPrice(row, PriceType.Net, showNeedsPrice),
      };
    },
    freight: function (row: FreightOrderLine, art: Article, warehouse: string) {
      const showNeedsPrice = isBendersEmployee && row.category == 'fee';

      return {
        class: 'dynamic',
        id: row.lineNumber,
        line: { value: row.lineNumber },
        _meta: { missingFromStep1: true },
        // image: { article: art },
        name_and_warehouse_status: [
          {
            _meta: undefined,
            component: 'name',
            name: 'name',
            label: row.customDescription?.required ? row.customDescription.value : getArticleTitle(art),
            article: art,
            batchnr: row.samNumber,
            // batchnr: row._meta
          },
          {
            _meta: undefined,
            component: 'warehouse_status',
            name: 'warehouse_status',
            selectable: false,
            removable: !this.isM3Order,
            region: this.region.value,
            article: art,
            warehouse: warehouse,
            customer_number: this.customerNumber.value,
            amount: row.quantity,
            unit: row.unit,
            type: 'checkout',
            allowAnyWarehouse: this.isM3Order === true,
          },
        ],
        quantity_unit_pieces: {
          component: 'string',
          article: art,
          value: `${row.quantity} ${convertUnit(row.unit || 'st', {
            type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
            lowercase: false,
          })}`,
        },
        weight: {
          value: art.chargeableWeight ? formatWeight(art.chargeableWeight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: getAddonPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: getAddonPrice(row, PriceType.Net, showNeedsPrice),
      };
    },
    standard: function (row: OrderProductlistRow, art: Article, warehouse: string, hidePrices: boolean) {
      // Try and keep the ID the same to limit re-drawing, and limit what needs to be animated
      // (If we start handling the orderlinenumber, we can remove this and just just orderlinenumber)
      const getQuantityAndUnitPieces = () => {
        const showAlternateUnit = !!row.unitDetails?.alternateUnit;
        const showSalesUnit =
          row.unitDetails?.salesUnit &&
          row.unitDetails.salesUnit !== row.unitDetails.baseUnit &&
          row.unitDetails.salesUnit !== row.unitDetails.alternateUnit;
        const showBaseUnit = !showAlternateUnit || row.unitDetails?.alternateUnit !== row.unitDetails?.baseUnit;

        const data = [] as any[];

        // Append the alternate unit if it exists
        if (row.unitDetails && showAlternateUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${roundUnit(row.unitDetails.alternateQuantity, 2)} ${convertUnit(
              row.unitDetails.alternateUnit || 'st',
              {
                type: !['p', 'p2'].includes(row.unitDetails.alternateUnit) ? 'short' : 'long',
                lowercase: false,
              }
            )}`,
          });
        }

        // Append the sales unit if it exists
        if (row.unitDetails && showSalesUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${roundUnit(row.unitDetails.salesQuantity, 2)} ${convertUnit(row.unitDetails.salesUnit || 'st', {
              type: !['p', 'p2'].includes(row.unitDetails.salesUnit) ? 'short' : 'long',
              lowercase: false,
            })}`,
          });
        }

        // Append the base unit
        if (showBaseUnit) {
          data.push({
            component: 'string',
            article: art,
            value: `${row.quantity} ${convertUnit(row.unit || 'st', {
              type: !['p', 'p2'].includes(row.unit) ? 'short' : 'long',
              lowercase: false,
            })}`,
          });
        }

        // Wrap each line in parathese, except the first one
        for (let i = 1; i < data.length; i++) {
          data[i].value = `(${data[i].value})`;
        }

        return data;
      };

      // BEN-2018: Hide warehouse stock for package items for M3 orders
      let showWarehouseStock = !isM3Order || art?.itemType !== 'PK' || +row.lineNumberPosition > 0;
      // BEN-2048: Hide stock for preallocated rows
      if (isM3Order && row.status && +row.status >= 33 && +row.status < 39) showWarehouseStock = false;

      const blockedPrice = { isBlocked: true, needsQuote: false, value: { price: null } };
      return {
        id: row.id ?? row.lineNumber,
        line: {
          value: row.lineNumber + (+row.lineNumberPosition > 0 ? ` ${row.lineNumberPosition}` : ''),
        },
        image: { article: art },
        name_and_warehouse_status: [
          {
            _meta: row._meta,
            component: 'name',
            name: 'name',
            label: getArticleTitle(art),
            article: art,
            batchnr: row.batchnr,
          },
          (showWarehouseStock && {
            _meta: row._meta,
            component: 'warehouse_status',
            name: 'warehouse_status',
            selectable: false,
            removable: !this.isM3Order,
            region: this.region.value,
            article: art,
            warehouse: warehouse,
            customer_number: this.customerNumber.value,
            amount: row.amount,
            unit: row.unit,
            batchnr: row.batchnr,
            type: 'checkout',
            allowAnyWarehouse: this.isM3Order === true,
          }) ||
            {},
        ],
        status: {
          value: row.status || '',
        },
        quantity_unit_pieces: getQuantityAndUnitPieces(),
        weight: {
          value: row.weight ? formatWeight(row.weight) + ` ${i18n.global.t('unit.short.kg')}` : '',
        },
        gross_price: hidePrices ? [blockedPrice] : getPrice(row, PriceType.Sales),
        discount: getDiscounts(row),
        net_price: hidePrices ? [blockedPrice] : getPrice(row, PriceType.Net),
        _is_underlying: row.isUnderlying === true,
      };
    },
  };
}

// export function buildOrderData(
//   delivery?: Delivery,
//   freight?: FreightFreightLine,
//   step1Rows: any[] = []
// ): any[] {
// }
interface deliveryMetadata {
  address: string | undefined;
  customerNumber: string | undefined;
  region: string | undefined;

  warehouse?: string; // TODO: Remove this? The line itself should have the correct warehouse
}

export interface FreightDeliveryOrderLine {
  id?: string;
  lineNumber: string;
  lineNumberPosition: string;
  itemNumber: string;
  batchNumber?: string;
  unit: string;
  quantity: number;
  jointDeliveryCode?: string;
  transactionReason: string;
  actionReason?: string;
  status?: string;
  configurationItem?: boolean;
  customDescription?: {
    required: boolean;
    value: string;
  };
  purchasePrice?: {
    required: boolean;
    value: number;
  };

  article?: Article;
  warehouse?: string;
  weight?: number;

  prices?: PriceCollection;
  price_manager?: PriceManager;
  price_patch?: FreightDeliveryOrderLinePatch;

  category?: string;

  comment?: string;

  // Used for price fetching
  // salesUnit?: string;
  priceList?: string;
  priceListOrigin?: string;
}

interface LinesWrapper {
  warehouse: string;
  lines: FreightDeliveryOrderLine[];
}

const calcAmount = (line): any => {
  return roundUnit(
    line.quantity * (line.article?.unit?.[line.unit || 'st'] || 0) || line.quantity,
    line.article.numberOfDecimalPlaces,
    false
  ) as number;
};

async function buildDeliveryRows(
  meta: deliveryMetadata,
  linesWrappers: LinesWrapper[],
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  const requiredArticles = new Set<string>();

  // Collect required articles
  for (const wrapper of linesWrappers) {
    for (const line of wrapper.lines) {
      if (line.itemNumber) requiredArticles.add(line.itemNumber);
    }
  }

  // Fetch articles that are not already in the cache
  const articles = Object.values(
    (await ArticleHelper.getAll({ artnrs: Array.from(requiredArticles) })) as Record<string, Article>
  ).reduce(
    (acc, art) => {
      acc[art.ArtNr] = art;
      return acc;
    },
    {} as Record<string, Article>
  );

  return linesWrappers.flatMap((wrapper) => {
    return wrapper.lines.map((line) => {
      const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
      const artnr = line.itemNumber;
      const art = articles[artnr];

      if (!art) {
        console.error('Missing article', artnr);
        return {};
      }

      line.id = existingRow?.id;
      line.article = art;
      line.warehouse = wrapper.warehouse;
      if (!line.weight) line.weight = existingRow?.weight ?? 0;
      line.actionReason = existingRow?.actionReason || line.actionReason || '';
      line.transactionReason = existingRow?.transactionReason ?? line.transactionReason ?? '';
      line.configurationItem = existingRow?.configurationItem || false;
      line.priceList = existingRow?.priceList;
      line.priceListOrigin = existingRow?.priceListOrigin;
      line.batchNumber = existingRow?.batchnr;
      line.unit = existingRow?.unit ?? line.unit;
      line.comment = existingRow?.comment;

      if (
        !existingRow &&
        line.category !== 'sam' &&
        (art.userDefinedField1Item?.trim() === 'FRAKT' || art.userDefinedField1Item?.trim() === 'PALL')
      ) {
        // if (!!line.price_manager) {
        //   console.debug('Re-using price_manager:', line.lineNumber, line.itemNumber);
        // } else {
        //   console.debug('Creating new price manager', line.lineNumberPosition, line.itemNumber);
        // }

        line.price_manager =
          line.price_manager ??
          new PriceManager(
            {
              address: meta.address ?? '',
              artnr: line.itemNumber,
              customer: meta.customerNumber ?? '',
              warehouse: wrapper.warehouse,
              region: meta.region ?? '',
              quantity: calcAmount(line),
              salespriceUnit: line.unit,
              unit: line.unit,
            },
            {
              net: line.prices?.price_net,
              sales: line.prices?.price_sales,
              m2: line.prices?.price_m2,
              line: line.prices?.price_line,
            }
          );
      } else if (existingRow) {
        // Never adopt a step1 row's price_manager for SAM rows, SAM rows get their own
        // PriceManager (with a SAMPriceFetcher) assigned by the freightConfig watcher.
        if (line.category !== 'sam') {
          if (line.quantity != existingRow.quantity) {
            console.warn(
              `Quantity mismatch for line ${line.lineNumber}: expected ${existingRow.quantity}, got ${line.quantity}. Not re-using step 1 price_manager.`
            );
            const retention = getRowRetentionStrategy(existingRow);
            if (retention) {
              let prices: PriceCollection | PriceObject | undefined = line?.prices;
              const getUpdatedPrice = (
                prices?: PriceCollection | PriceObject,
                existingRow?: OrderProductlistRow
              ): PriceObject => {
                const pm = existingRow?.price_manager;
                const price_net: Price = (prices as PriceCollection)?.price_net || (prices as PriceObject)?.net;
                const price_sales: Price = (prices as PriceCollection)?.price_sales || (prices as PriceObject)?.sales;
                const price_m2: LinePrice = (prices as PriceCollection)?.price_m2 || (prices as PriceObject)?.m2;
                const price_line: LinePrice = (prices as PriceCollection)?.price_line || (prices as PriceObject)?.line;

                if (pm) {
                  if (price_line) {
                    for (let i = 1; i <= 6; i++) {
                      const d = pm.getDiscount(PriceType.Line, i as any);
                      price_line[`discount${i}`] = d?.discount;
                      price_line[`statusDiscount${i}`] = d?.status;
                      price_line[`discountText${i}`] = d?.text;
                    }
                    if (retention == RowRetentionStrategy.Discounts) {
                      price_line.lineAmount =
                        new Pricing(price_line.lineAmount, price_line.currency).withDiscounts(
                          pm.getAllDiscounts(PriceType.Line)
                        ).value ?? 0;
                    } else {
                      price_line.salesPrice = pm.getPrice(PriceType.Line).value ?? 0;
                      price_line.lineAmount = roundUnit(price_line.salesPrice * line.quantity, 4, false) as number;
                    }
                  }
                  if (price_net) {
                    price_net.discount = pm.getTotalDiscount(PriceType.Line);
                    if (retention == RowRetentionStrategy.Discounts) {
                      price_net.netPrice =
                        new Pricing(price_net.netPrice, price_net.currency).withDiscounts(
                          pm.getAllDiscounts(PriceType.Line)
                        ).value ?? 0;
                      price_net.price_total =
                        new Pricing(price_net.price_total, price_net.currency).withDiscounts(
                          pm.getAllDiscounts(PriceType.Line)
                        ).value ?? 0;
                    } else {
                      price_net.netPrice = pm.getPrice(PriceType.Net).value ?? 0;
                      price_net.salesPrice = pm.getPrice(PriceType.Sales).value ?? 0;
                      price_net.price_total = roundUnit(price_net.netPrice * line.quantity, 4, false) as number;
                    }
                  }
                  if (price_m2) {
                    for (let i = 1; i <= 6; i++) {
                      const d = pm.getDiscount(PriceType.M2, i as any);
                      price_m2[`discount${i}`] = d?.discount;
                      price_m2[`statusDiscount${i}`] = d?.status;
                      price_m2[`discountText${i}`] = d?.text;
                    }
                    if (retention == RowRetentionStrategy.Discounts) {
                      price_m2.lineAmount =
                        new Pricing(price_m2.lineAmount, price_m2.currency).withDiscounts(
                          pm.getAllDiscounts(PriceType.Line)
                        ).value ?? 0;
                    } else {
                      price_m2.salesPrice = pm.getPrice(PriceType.M2).value ?? 0;
                      price_m2.lineAmount = roundUnit(price_m2.salesPrice * line.quantity, 4, false) as number;
                    }
                  }
                }

                return {
                  net: price_net,
                  sales: price_sales,
                  m2: price_m2,
                  line: price_line,
                };
              };

              line.price_manager = new PriceManager(
                {
                  address: meta.address ?? '',
                  artnr: line.itemNumber,
                  customer: meta.customerNumber ?? '',
                  warehouse: wrapper.warehouse,
                  region: meta.region ?? '',
                  quantity: calcAmount(line),
                  salespriceUnit: line.unit,
                  unit: line.unit,
                },
                getUpdatedPrice(prices, existingRow)
              );
            } else {
              line.price_manager = new PriceManager(
                {
                  address: meta.address ?? '',
                  artnr: line.itemNumber,
                  customer: meta.customerNumber ?? '',
                  warehouse: wrapper.warehouse,
                  region: meta.region ?? '',
                  quantity: calcAmount(line),
                  salespriceUnit: line.unit,
                  unit: line.unit,
                },
                {
                  net: line.prices?.price_net,
                  sales: line.prices?.price_sales,
                  m2: line.prices?.price_m2,
                  line: line.prices?.price_line,
                }
              );
            }
          } else {
            line.price_manager = existingRow.price_manager ?? line.price_manager;
          }
        }

        if (!line.price_manager && line.category !== 'sam') {
          console.error('Missing price manager', line);
        }
      }

      return line;
    });
  }) as FreightDeliveryOrderLine[];
}

async function getFreightDeliveryRows(
  freightConfig: FreightConfiguration,
  meta: deliveryMetadata,
  deliveryWarehouses?: DeliveryMethodWarehouse[],
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  const linesWrapper = [] as {
    warehouse: string;
    lines: FreightDeliveryOrderLine[];
  }[];
  // Iterate all deliveries and their warehouses to get the order lines and articles
  for (const deliveryWarehouse of deliveryWarehouses ?? []) {
    for (const delivery of deliveryWarehouse.delivery ?? []) {
      linesWrapper.push({
        warehouse: deliveryWarehouse.warehouse,
        lines: delivery.orderLines.map((line) => {
          return {
            lineNumber: line.lineNumber,
            lineNumberPosition: line.lineNumberPosition,
            itemNumber: line.itemNumber,
            category: line.category,
            unit: line.unit,
            quantity: line.quantity,
            transactionReason: line.transactionReason,
            jointDeliveryCode: delivery.jointDeliveryCode,
            warehouse: deliveryWarehouse.warehouse,
            status: line.status,
            weight: line?.weight,
            prices: line.prices,
            price_manager: line.price_manager,
            actionReason: line.actionReason,
            customDescription: line.customDescription,
            purchasePrice: line.purchasePrice,
            samNumber: line.samNumber,
          };
        }),
      });
    }
  }

  const lines = await buildDeliveryRows(meta, linesWrapper, step1Rows);

  // Price update handling
  const pendingRequests: Promise<void>[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const config = freightConfig?.deliveryConfigs?.find(
      (config) => config.jointDeliveryCode == line.jointDeliveryCode && config.warehouse.id == line.warehouse
    );

    if ((line.actionReason == '' || line.category == 'sam') && config && line.price_manager) {
      const [date, m3Date] = config.getDeliveryDate();
      line.price_manager.update_attributes({
        date: date !== m3Date ? date : undefined,
        priceList: line.priceList,
        priceListOrigin: line.priceListOrigin,
      });
      pendingRequests.push(line.price_manager.awaitFetching());
    }
  }

  // Await price fetching
  // TODO: Check why this isn't reactive once it finishes,
  // because currently we don't show that we are loading
  await Promise.all(pendingRequests);

  return lines;
}

async function getPackagingLineRows(
  meta: deliveryMetadata,
  packaging?: PackagingResponse,
  step1Rows: OrderProductlistRow[] = []
): Promise<any[]> {
  const linesWrapper = [] as {
    warehouse: string;
    lines: FreightDeliveryOrderLine[];
  }[];
  const lines = [] as any[];
  for (const line of packaging?.orderPackagingResponseOrderLines ?? []) {
    const existingRow = matchOrderRow(step1Rows ?? [], line);
    const artnr = existingRow?.itemNumber ?? line.itemNumber;
    lines.push({
      lineNumber: line.lineNumber,
      lineNumberPosition: line.lineNumberPosition,
      itemNumber: artnr,
      unit: line.unit,
      quantity: line.quantity,
      prices: line.prices,
      price_manager: line.price_manager,
    });
  }
  for (const line of packaging?.orderPackagingResponsePackageLines ?? []) {
    lines.push({
      lineNumber: line.lineNumber,
      lineNumberPosition: line.lineNumberPosition,
      itemNumber: line.itemNumber,
      unit: line.unit,
      quantity: line.quantity,
      prices: line.prices,
      price_manager: line.price_manager,
    });
  }
  linesWrapper.push({
    warehouse: meta.warehouse ?? '',
    lines: lines,
  });

  return await buildDeliveryRows(meta, linesWrapper, step1Rows);
}

export async function getRows(
  freightConfig: FreightConfiguration,
  meta: deliveryMetadata,
  input?: FreightDeliveryMethod | PackagingResponse,
  step1Rows: OrderProductlistRow[] = []
): Promise<FreightDeliveryOrderLine[]> {
  if (!input) return [];

  if ('warehouses' in input) {
    return getFreightDeliveryRows(
      freightConfig,
      meta as deliveryMetadata,
      input?.warehouses,
      step1Rows.filter((r) => r.isUnderlying !== true)
    );
  } else if ('orderPackagingResponseOrderLines' in input) {
    return getPackagingLineRows(
      meta as deliveryMetadata,
      input,
      step1Rows.filter((r) => r.isUnderlying !== true)
    );
  }

  return [];
}

export async function formatPackagingListRows(
  h: RowHandlers,
  packaging?: PackagingResponse,
  step1Rows: any[] = [],
  isM3Order: boolean = false
): Promise<ProductlistRow[]> {
  const lines = await getPackagingLineRows(
    {
      address: h.address?.value,
      customerNumber: h.customerNumber.value,
      region: h.region.value,
      warehouse: h.warehouse?.value ?? '',
    },
    packaging,
    step1Rows.filter((r) => r.isUnderlying !== true)
  );

  let totalWeight = 0;
  const data: any[] = lines
    .map((line) => {
      // Handle articles based on their 'Type' field
      const warehouse = line.warehouse ?? h.warehouse ?? '';
      switch (line.article?.userDefinedField1Item?.trim()) {
        case 'PALL':
          totalWeight += line.article.chargeableWeight * Math.ceil(line.quantity);
          return h.packaging(line, line.article, h.warehouse?.value ?? '');
        case 'FRAKT':
          // Should never happen, but log it if it does
          console.warn('FRAKT line in packaging, skipping as this should not happen', line);
          return {};
        default:
          const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
          // Requires a row from step 1 to be present
          if (!existingRow) {
            console.error('Missing row', line);
            return {};
          }

          if (!line.lineNumber || !line.article) {
            console.error('Missing line number or article', line);
            return {};
          }

          totalWeight += +(existingRow.weight || 0);

          // Re-calculate the alternate and salesUnit amounts as quantity might have changed
          if (existingRow.unitDetails && existingRow.unitDetails.baseQuantity != line.quantity) {
            const baseQuantity = existingRow.unitDetails.baseQuantity;

            if (existingRow.unitDetails.salesUnit) {
              const salesQuantity = existingRow.unitDetails.salesQuantity;
              existingRow.unitDetails.salesQuantity = line.quantity / (baseQuantity / salesQuantity);
            }
            if (existingRow.unitDetails.alternateUnit) {
              const alternateQuantity = existingRow.unitDetails.alternateQuantity;
              existingRow.unitDetails.alternateQuantity = line.quantity / (baseQuantity / alternateQuantity);
            }
          }

          let hidePrices = isM3Order && isUnderlyingPKRow(lines, existingRow);

          // Update the existing row with the new values
          // existingRow.quantity = line.quantity;
          existingRow.unit = line.unit;
          existingRow.lineNumber = line.lineNumber;

          return h.standard(existingRow, line.article, warehouse, hidePrices);
      }
    })
    .filter((l) => l._is_underlying !== true);

  // Append a row with total weight
  if (!h.mobile.value) {
    const total = totalWeight?.toFixed(2);

    const formattedWeight = !isNaN(totalWeight) ? formatWeight(total) : '...';

    data.push({
      class: 'dynamic',
      weight: {
        class: 'has-text-weight-bold is-size-7 nowrap',
        value: formattedWeight + ` ${i18n.global.t('unit.short.kg')}`,
      },
    });
  }

  return data;
}

export async function formatFreightListRows(
  h: RowHandlers,
  freightConfig: FreightConfiguration,
  delivery?: FreightDelivery,
  deliveryWarehouse?: DeliveryMethodWarehouse,
  step1Rows: any[] = [],
  orderLinePatch?: LineKeyMap<FreightDeliveryOrderLinePatch>
): Promise<ProductlistRow[]> {
  const lines = await getFreightDeliveryRows(
    freightConfig,
    {
      address: h.address?.value,
      customerNumber: h.customerNumber.value,
      region: h.region.value,
      warehouse: deliveryWarehouse?.warehouse ?? '',
    },
    [
      {
        warehouse: deliveryWarehouse?.warehouse ?? '',
        delivery: delivery ? [delivery] : [],
      },
    ],
    step1Rows.filter((r) => r.isUnderlying !== true)
  );

  // console.log('lines', lines);

  let totalWeight = 0;
  const data: any[] = lines.map((line) => {
    // Handle articles based on their 'Type' field
    const warehouse = line.warehouse ?? deliveryWarehouse?.warehouse ?? '';
    line.price_patch = orderLinePatch?.get(new LineKey(line)) ?? undefined;
    switch (line.article?.userDefinedField1Item.trim()) {
      case 'PALL':
        totalWeight += line.article.chargeableWeight * Math.ceil(line.quantity);
        return h.packaging(line as FreightOrderLine, line.article, warehouse);
      case 'FRAKT':
        return h.freight(line as FreightOrderLine, line.article, warehouse);
      default:
        const existingRow = matchOrderRow(step1Rows ?? [], line as FreightOrderLine);
        // Requires a row from step 1 to be present
        if (!existingRow) {
          console.error('Missing row', line);
          return {};
        }

        if (!line.lineNumber || !line.article) {
          console.error('Missing line number or article', line);
          return {};
        }

        // Order lines that are split over many deliveries will get each delivery's line weight
        // in delivery lines. We pass it through the freight object
        if (line.weight) existingRow.weight = line.weight;

        if (!existingRow?.isUnderlying || +existingRow.lineNumberPosition == 0) {
          totalWeight += +(existingRow.weight || 0);
        }

        // BEN-2079: Hide weights of overlying rows
        if (existingRow.article?.underlyingItems?.length) {
          existingRow.weight = undefined;
        }

        // Re-calculate the alternate and salesUnit amounts as quantity might have changed
        if (existingRow.unitDetails && existingRow.unitDetails.baseQuantity != line.quantity) {
          const baseQuantity = existingRow.unitDetails.baseQuantity;

          if (existingRow.unitDetails.salesUnit) {
            const salesQuantity = existingRow.unitDetails.salesQuantity;
            existingRow.unitDetails.salesQuantity = line.quantity / (baseQuantity / salesQuantity);
          }
          if (existingRow.unitDetails.alternateUnit) {
            const alternateQuantity = existingRow.unitDetails.alternateQuantity;
            existingRow.unitDetails.alternateQuantity = line.quantity / (baseQuantity / alternateQuantity);
          }
        }

        let hidePrices = isUnderlyingPKRow(lines, existingRow);

        // Update the existing row with the new values
        // existingRow.quantity = line.quantity;
        existingRow.unit = line.unit;
        existingRow.lineNumber = line.lineNumber;
        existingRow.status = line.status;
        existingRow.price_manager = line.price_manager;

        return h.standard(existingRow, line.article, warehouse, hidePrices);
    }
  });

  // Append a row with total weight
  if (!h.mobile.value) {
    const total = totalWeight?.toFixed(2);

    const formattedWeight = !isNaN(totalWeight) ? formatWeight(total) : '...';

    data.push({
      class: 'dynamic',
      weight: {
        class: 'has-text-weight-bold is-size-7 nowrap',
        value: formattedWeight + ` ${i18n.global.t('unit.short.kg')}`,
      },
    });
  }

  return data;
}

/** Check if an order row is a pallet or freight article in freight calc */
export function isRowPalletOrFreight(article: Article): boolean {
  return ['FRAKT', 'PALL'].includes(article?.userDefinedField1Item?.trim() ?? '');
}

function isUnderlyingPKRow(rows: any[], row: OrderProductlistRow): boolean {
  // BEN-2057: If overlying row is a package article, hide prices
  if (+row.lineNumberPosition > 0) {
    const overlying = rows.find((r) => r.lineNumber == row.lineNumber && r.lineNumberPosition == '0');
    if (!overlying) return false;

    if (overlying.article.itemType == 'PK') return true;
  }
  return false;
}
